import type { LatLng } from "./api";
import { coordinate } from "./location";

export const DIR_URL = "https://www.google.com/maps/dir/?api=1";
/** Google Maps URLs: the Maps app and a desktop browser take 9 waypoints, a phone browser takes 3. */
export const MAX_WAYPOINTS = 9;
export const PHONE_WAYPOINTS = 3;
export const MAX_STOPS = 50;
const EARTH_M = 6_371_000;

export interface Stop extends LatLng {
  id: string;
  name: string;
  address: string;
}

export function haversineM(a: LatLng, b: LatLng): number {
  const rad = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Nearest first from the start. With no start, the first stop stays first and is the start. */
export function nearestNeighbourOrder<T extends LatLng>(stops: readonly T[], start: LatLng | null): T[] {
  if (stops.length === 0) return [];
  const left = [...stops];
  const ordered: T[] = start ? [] : [left.shift() as T];
  let at: LatLng = start ?? ordered[0];
  while (left.length) {
    let best = 0;
    for (let index = 1; index < left.length; index += 1) {
      if (haversineM(at, left[index]) < haversineM(at, left[best])) best = index;
    }
    const [next] = left.splice(best, 1);
    ordered.push(next);
    at = next;
  }
  return ordered;
}

/** The legs of a route: each leg has at most `maxWaypoints` waypoints, and starts at the end of the leg before. */
export function routeLegs<T>(points: readonly T[], maxWaypoints = MAX_WAYPOINTS): T[][] {
  if (points.length <= 1) return points.length ? [[...points]] : [];
  const step = maxWaypoints + 1;
  const legs: T[][] = [];
  for (let from = 0; from < points.length - 1; from += step) legs.push(points.slice(from, Math.min(from + step, points.length - 1) + 1));
  return legs;
}

const place = (point: LatLng) => `${coordinate(point.lat)},${coordinate(point.lng)}`;

/** One Google Maps directions link. A leg of one point has no origin, so Google starts from the phone. */
export function googleDirUrl(leg: readonly LatLng[]): string {
  if (leg.length === 0) return DIR_URL;
  const destination = `&destination=${place(leg[leg.length - 1])}`;
  if (leg.length === 1) return DIR_URL + destination;
  const waypoints = leg.slice(1, -1).map(place).join("%7C");
  return `${DIR_URL}&origin=${place(leg[0])}${destination}${waypoints ? `&waypoints=${waypoints}` : ""}`;
}

export const googleDirUrls = (points: readonly LatLng[], maxWaypoints = MAX_WAYPOINTS) => routeLegs(points, maxWaypoints).map(googleDirUrl);

export const legWaypoints = (narrow: boolean) => (narrow ? PHONE_WAYPOINTS : MAX_WAYPOINTS);

/** The points of the route in order: the GPS start (when used), then the stops. */
export const routePoints = (stops: readonly LatLng[], start: LatLng | null): LatLng[] => (start ? [start, ...stops] : [...stops]);

export function moveStop<T>(stops: readonly T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (index < 0 || index >= stops.length || to < 0 || to >= stops.length) return [...stops];
  const next = [...stops];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

/** Add the stops that are not on the route yet, to the limit; the count that did not fit is `left`. */
export function addStops<T extends { id: string }>(stops: readonly T[], more: readonly T[], limit = MAX_STOPS): { stops: T[]; left: number } {
  const have = new Set(stops.map((stop) => stop.id));
  const fresh = more.filter((stop) => !have.has(stop.id));
  const room = Math.max(0, limit - stops.length);
  return { stops: [...stops, ...fresh.slice(0, room)], left: Math.max(0, fresh.length - room) };
}

/** A cell that a spreadsheet cannot read as a formula. */
function csvCell(value: string | number): string {
  let text = String(value);
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function routeCsv(stops: readonly Stop[]): string {
  const rows = [["order", "name", "address", "lat", "lng"], ...stops.map((stop, index) => [index + 1, stop.name, stop.address, Number(coordinate(stop.lat)), Number(coordinate(stop.lng))])];
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}
